import React, { useState } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { Loader2 } from 'lucide-react';
import { usePermissions } from '../../../hooks/usePermissions';
import { canAssignEmployeeSkills } from '../../../utils/roleChecks';
 
// Hooks
import { useEmployees } from './hooks/useEmployees';
import { useWorkday } from './hooks/useWorkday';
import { useRoleAssignment } from './hooks/useRoleAssignment';
 
// Componentes
import { EmployeeFiltersModal } from './components/EmployeeFiltersModal';
import { WorkdayEmployeeDetailModal } from './components/WorkdayEmployeeDetailModal';
import { EmployeePickerTable } from './components/EmployeePickerTable';
import { EmployeeDetails } from './components/EmployeeDetails';
import { WorkdayEmployeeTable } from './components/WorkdayEmployeeTable';
import { RoleAssignmentForm } from './components/RoleAssignmentForm';
import { AssignedRolesList } from './components/AssignedRolesList';
import { EmployeeSkillsSection } from './components/EmployeeSkillsSection';
import { SkillsCatalogSection } from './components/SkillsCatalogSection';
 
const Empleados = () => {
  const { t } = useLanguage();
  const { roleKeys } = usePermissions();
  const canAssignSkills = canAssignEmployeeSkills(roleKeys);
  const [selectedEmployeeForRole, setSelectedEmployeeForRole] = useState(null);
  const [showCreateSkillModal, setShowCreateSkillModal] = useState(false);
 
  // Hook para gestión de empleados
  const employeesHook = useEmployees();
  const {
    employees,
    loading,
    error,
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    employeesToShow,
    totalItems,
    totalPages,
    pageSize,
    setPageSize,
    pageSizeOptions,
    rangeStart,
    rangeEnd,
    loadEmployees,
    selectedEmployee,
    setSelectedEmployee,
    loadEmployeeDetails,
    filters,
    setFilters,
    showFiltersModal,
    setShowFiltersModal,
    handleApplyFilters
  } = employeesHook;
 
  // Hook para gestión de Workday
  const workdayHook = useWorkday(() => loadEmployees(false));
  const {
    workdayLoading,
    workdayError,
    workdayEmployees,
    workdayEmployeesToShow,
    workdayTotalItems,
    workdayTotalPages,
    workdayPage,
    setWorkdayPage,
    workdayPageSize,
    setWorkdayPageSize,
    workdayPageSizeOptions,
    workdayRangeStart,
    workdayRangeEnd,
    syncing,
    selectedWorkdayEmployee,
    showWorkdayDetailModal,
    setShowWorkdayDetailModal,
    handleSyncWorkday,
    handleProcessWorkday,
    loadWorkdayEmployeeDetails
  } = workdayHook;
 
  // Hook para asignación de roles
  const roleAssignmentHook = useRoleAssignment(
    selectedEmployeeForRole,
    () => loadEmployees(false)
  );
  const {
    assignmentForm,
    setAssignmentForm,
    assignmentOptionsLoading,
    assignmentOptionsError,
    assigning,
    assignmentSuccessMessage,
    assignmentErrorMessage,
    employeeAssignments,
    assignmentsLoading,
    assignmentsError,
    assignmentFactories,
    assignmentDepartments,
    assignmentRoles,
    handleAssignRole,
    handleUnassignRole,
    handleSyncToAuthentik,
    loadEmployeeAssignments
  } = roleAssignmentHook;
 
  // Handlers
  const handleEmployeeClick = (employee) => {
    setSelectedEmployeeForRole(employee);
    setAssignmentForm({ factoryId: '', departmentId: '', roleId: '' });
    loadEmployeeAssignments(employee.id);
    // Seleccionar el empleado para mostrar sus detalles (ya están incluidos en los datos)
    if (employee.id) {
      loadEmployeeDetails(employee.id);
    }
  };

  return (
    <div className="p-4 sm:p-6 w-full min-w-0">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">
          {t.personas?.empleados?.title || '🙍‍♂️ Empleados'}
        </h1>
        <p className="text-gray-400">
          {t.personas?.empleados?.description || 'Gestión y visualización de empleados'}
        </p>
      </div>
 
      {loading && (
        <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg p-12">
          <div className="flex flex-col items-center justify-center text-gray-400">
            <Loader2 className="animate-spin mb-4" size={32} />
            <p>{t.common?.loading || 'Cargando empleados...'}</p>
          </div>
        </div>
      )}
 
      {error && !loading && (
        <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg p-6 mb-6">
          <div className="rounded-lg border border-red-800 bg-red-900/20 p-4">
            <p className="text-red-300">
              <strong>{t.common?.error || 'Error'}:</strong> {error}
            </p>
          </div>
        </div>
      )}
 
      {/* Sección de Empleados de Workday */}
      <WorkdayEmployeeTable
        workdayLoading={workdayLoading}
        workdayError={workdayError}
        workdayEmployees={workdayEmployees}
        workdayEmployeesToShow={workdayEmployeesToShow}
        workdayTotalItems={workdayTotalItems}
        workdayTotalPages={workdayTotalPages}
        workdayPage={workdayPage}
        setWorkdayPage={setWorkdayPage}
        workdayPageSize={workdayPageSize}
        setWorkdayPageSize={setWorkdayPageSize}
        workdayPageSizeOptions={workdayPageSizeOptions}
        workdayRangeStart={workdayRangeStart}
        workdayRangeEnd={workdayRangeEnd}
        syncing={syncing}
        onSync={handleSyncWorkday}
        onEmployeeClick={loadWorkdayEmployeeDetails}
        onProcess={handleProcessWorkday}
      />
 
      {/* Layout de dos columnas: Tabla izquierda y Detalles derecha */}
      {!loading && !error && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-stretch">
          {/* Columna izquierda: Tabla de empleados */}
          <div className="min-w-0 flex flex-col min-h-0">
            <EmployeePickerTable
              employees={employees}
              employeesToShow={employeesToShow}
              totalItems={totalItems}
              totalPages={totalPages}
              page={page}
              setPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              pageSizeOptions={pageSizeOptions}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedEmployeeForRole={selectedEmployeeForRole}
              onEmployeeClick={handleEmployeeClick}
              onFiltersClick={() => setShowFiltersModal(true)}
            />
          </div>
 
          {/* Columna derecha: Detalles del empleado */}
          <div className="min-w-0 flex flex-col min-h-0 lg:sticky lg:top-6">
            <EmployeeDetails
              employee={selectedEmployee}
              loading={false}
            />
          </div>
        </div>
      )}
 
      {/* Sección Roles Asignados */}
      {!loading && !error && selectedEmployeeForRole && (
        <AssignedRolesList
          selectedEmployee={selectedEmployeeForRole}
          assignmentsLoading={assignmentsLoading}
          assignmentsError={assignmentsError}
          employeeAssignments={employeeAssignments}
          onUnassignRole={handleUnassignRole}
          onSyncToAuthentik={handleSyncToAuthentik}
        />
      )}
 
      {/* Sección Asignación de roles */}
      {!loading && !error && selectedEmployeeForRole && (
        <RoleAssignmentForm
          selectedEmployee={selectedEmployeeForRole}
          assignmentForm={assignmentForm}
          setAssignmentForm={setAssignmentForm}
          assignmentOptionsLoading={assignmentOptionsLoading}
          assignmentOptionsError={assignmentOptionsError}
          assignmentFactories={assignmentFactories}
          assignmentDepartments={assignmentDepartments}
          assignmentRoles={assignmentRoles}
          assigning={assigning}
          assignmentSuccessMessage={assignmentSuccessMessage}
          assignmentErrorMessage={assignmentErrorMessage}
          onAssign={handleAssignRole}
        />
      )}

      {/* Sección Habilidades del empleado (tras roles). RRHH y Operational Technology. */}
      {!loading && !error && canAssignSkills && selectedEmployeeForRole && (
        <EmployeeSkillsSection
          selectedEmployee={selectedEmployeeForRole}
          onSkillsChanged={() => loadEmployees(false)}
          onCreateSkill={() => setShowCreateSkillModal(true)}
        />
      )}

      {/* Catálogo global de habilidades */}
      {!loading && !error && (
        <SkillsCatalogSection
          createModalOpen={showCreateSkillModal}
          onCreateModalChange={setShowCreateSkillModal}
        />
      )}

      {/* Modales */}
      <EmployeeFiltersModal
        isOpen={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
        employees={employees}
        filters={filters}
        onApplyFilters={handleApplyFilters}
      />
      <WorkdayEmployeeDetailModal
        isOpen={showWorkdayDetailModal}
        onClose={() => {
          setShowWorkdayDetailModal(false);
        }}
        employee={selectedWorkdayEmployee}
      />
    </div>
  );
};
 
export default Empleados;
 
 